import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { XbrlFilingData } from 'src/app/models/xbrlFilingData.model';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { OnlyLettersAndNumbersValidator } from 'src/app/directives/only-letters-and-numbers-validator.directive';
import { CannotContainOnlySpaceValidator } from 'src/app/directives/cannot-contain-only-space-validator.directive';
import { FilingDataApiClient } from 'src/app/services/api-clients/filing-data.api-client';
import { IsUnsetCalendarDate, ParseCalendarDate, ToCalendarDateString } from 'src/app/_helpers/calendarDateHelper';

export const EditFilingDataModalOptions: NgbModalOptions = {
  backdrop: 'static',
  keyboard: false,
  centered: true,
  size: 'editdocument'
};

export enum LegalEntityRegisteredTypeEnum {
  Micro = 'Micro',
  Small = 'Small',
  Medium = 'Medium',
  Large = 'Large'
}

/** The KvK file is rendered from a dutch or an english template, nothing else */
export enum FilingDataLanguageEnum {
  Nl = 'nl',
  En = 'en'
}

/**
 * The fields the API computes 'isValid' from, paired with the label the form shows them under.
 * Kept in form order so the warning reads top to bottom like the form does.
 * <p>'language' is left out on purpose: it counts towards the API's isValid, but the form always carries one
 * of the two templates, so it can never be the thing that is missing.</p>
 */
const RequiredFilingDataFields: { field: keyof XbrlFilingData, labelKey: string }[] = [
  { field: 'legalEntityName', labelKey: 'Modals.EditFilingData.LegalEntityName' },
  { field: 'legalEntityLegalForm', labelKey: 'Modals.EditFilingData.LegalForm' },
  { field: 'legalEntityRegisteredOffice', labelKey: 'Modals.EditFilingData.RegisteredOfficePlace' },
  { field: 'chamberOfCommerceRegistrationNumber', labelKey: 'Modals.EditFilingData.ChamberOfCommerceRegistrationNumber' },
  { field: 'legalEntityRegisteredType', labelKey: 'Modals.EditFilingData.LegalEntityRegisteredType' },
  { field: 'financialReportingPeriodEndDate', labelKey: 'Modals.EditFilingData.FinancialReportingPeriodEndDate' },
  { field: 'financialReportingPeriod', labelKey: 'Modals.EditFilingData.FinancialReportingPeriod' }
];

@Component({
    selector: 'app-edit-filing-data-modal',
    templateUrl: './edit-filing-data-modal.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditFilingDataModalComponent implements OnInit {
  @Input() requestId!: string;
  @Input() filingData!: XbrlFilingData;
  @Output() closeModal: EventEmitter<XbrlFilingData | null> = new EventEmitter<XbrlFilingData | null>();
  legalEntityRegisteredTypeEnum = LegalEntityRegisteredTypeEnum;
  filingDataLanguageEnum = FilingDataLanguageEnum;
  xbrlFilingData!: XbrlFilingData;
  form: UntypedFormGroup;
  chamberOfCommerceRegistrationNumberControl: FormControl;
  financialReportingPeriodControl: FormControl;
  financialReportingPeriodEndDateControl: FormControl;
  legalEntityLegalFormControl: FormControl;
  legalEntityNameControl: FormControl;
  legalEntityRegisteredOfficeControl: FormControl;
  isAcceptedControl: FormControl;

  // Derived state the template binds to, held as fields and recomputed by refreshDerivedState after anything
  // that can change the filing data.
  //
  // Deliberately NOT getters: a getter that builds a new array or a new Date hands the template a fresh
  // identity on every change detection pass. For the @for below that re-creates its DOM every pass, and for
  // the date pickers it makes their input look changed every pass - which, since the picker two-way binds that
  // same value with ngModel, loops change detection until the modal locks up.

  /** The labels of the required fields that still carry no value, mirroring the API's 'isValid' */
  missingFieldLabels: string[] = [];
  /** Undefined rather than year 1, so the picker opens empty instead of on january 0001 */
  financialReportingPeriodEndDateForPicker?: Date;
  documentAdoptionDateForPicker?: Date;

  /** Held while the adoption says 'no', so answering 'yes' again gives the user their date back */
  private setAsideAdoptionDate?: Date;

  translations: any = <any>{};

  constructor(private toastService: ToastService,
              private translateService: TranslateService,
              private formBuilder: UntypedFormBuilder,
              private filingDataApiClient: FilingDataApiClient) {
    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
    this.chamberOfCommerceRegistrationNumberControl = new FormControl('', [Validators.required, Validators.maxLength(50), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.financialReportingPeriodControl = new FormControl('', [Validators.required, Validators.maxLength(50), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.financialReportingPeriodEndDateControl = new FormControl('', [Validators.required, Validators.maxLength(50), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.legalEntityLegalFormControl = new FormControl('', [Validators.required, Validators.maxLength(50), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.legalEntityNameControl = new FormControl('', [Validators.required, Validators.maxLength(255)]);
    this.legalEntityRegisteredOfficeControl = new FormControl('' , [Validators.required, Validators.maxLength(50), OnlyLettersAndNumbersValidator(), CannotContainOnlySpaceValidator()]);
    this.isAcceptedControl = new FormControl(false);

    // The yes/no answers and the two dropdowns below are written straight onto the model by setDropdownValue.
    // They must stay out of this group: valueChanges copies every control's value onto the model, so a control
    // that no template writes to would overwrite the chosen answer with its own untouched empty value.
    this.form = this.formBuilder.group({
      chamberOfCommerceRegistrationNumber: this.chamberOfCommerceRegistrationNumberControl,
      financialReportingPeriod: this.financialReportingPeriodControl,
      financialReportingPeriodEndDate: this.financialReportingPeriodEndDateControl,
      legalEntityLegalForm: this.legalEntityLegalFormControl,
      legalEntityName: this.legalEntityNameControl,
      legalEntityRegisteredOffice: this.legalEntityRegisteredOfficeControl,
      isAccepted: this.isAcceptedControl
    });
  }

  getTranslations() {
    this.translateService.get([
      'Toasts.Success',
      'Toasts.FilingDataUpdated',
      'Toasts.Error',
      'Toasts.FilingDataUpdatedFailed',
    ]).subscribe(translation => {
      this.translations.success = translation['Toasts.Success'];
      this.translations.filingDataUpdated = translation['Toasts.FilingDataUpdated'];
      this.translations.error = translation['Toasts.Error'];
      this.translations.filingDataUpdateFailed = translation['Toasts.FilingDataUpdatedFailed'];
    });
  }

  ngOnInit(): void {
    if(this.filingData) {
      this.xbrlFilingData = {...this.filingData};
      this.determineLegalEntityRegisteredType(this.xbrlFilingData.legalEntityRegisteredType);
      this.determineLanguage(this.xbrlFilingData.language);
      // the date is not nullable on the API, an unset one must not come through as year 1
      if (IsUnsetCalendarDate(this.xbrlFilingData.financialReportingPeriodEndDate)) {
        this.xbrlFilingData.financialReportingPeriodEndDate = undefined as unknown as Date;
      }

      this.legalEntityNameControl.setValue(this.xbrlFilingData.legalEntityName);
      this.legalEntityLegalFormControl.setValue(this.xbrlFilingData.legalEntityLegalForm);
      this.legalEntityRegisteredOfficeControl.setValue(this.xbrlFilingData.legalEntityRegisteredOffice);
      this.chamberOfCommerceRegistrationNumberControl.setValue(this.xbrlFilingData.chamberOfCommerceRegistrationNumber);
      this.financialReportingPeriodEndDateControl.setValue(this.xbrlFilingData.financialReportingPeriodEndDate);
      this.isAcceptedControl.setValue(this.xbrlFilingData.isAccepted ?? false);

      this.financialReportingPeriodControl.setValue(this.xbrlFilingData.financialReportingPeriod);
    }

    // Automatically synchronize xbrlFilingData with the form
    this.form.valueChanges.subscribe(values => {
      Object.assign(this.xbrlFilingData, values);
      this.refreshDerivedState();
    });

    this.refreshDerivedState();
  }

  /**
   * Recomputes what the template binds, after anything that can change the filing data. Identity is preserved
   * wherever the value did not actually move, so a bound picker input does not look changed for nothing.
   */
  private refreshDerivedState(): void {
    if (!this.xbrlFilingData) {
      return; // opened without filing data, so there is nothing to derive from
    }
    this.missingFieldLabels = RequiredFilingDataFields
      .filter(x => this.isFieldMissing(x.field))
      .map(x => x.labelKey);
    this.financialReportingPeriodEndDateForPicker = this.sameDateOrNew(
      this.financialReportingPeriodEndDateForPicker, this.xbrlFilingData.financialReportingPeriodEndDate);
    this.documentAdoptionDateForPicker = this.sameDateOrNew(
      this.documentAdoptionDateForPicker, this.xbrlFilingData.documentAdoptionDate);
  }

  /** The very same Date instance back when the value did not move, so bound inputs keep a stable identity */
  private sameDateOrNew(current: Date | undefined, value: Date | string | undefined): Date | undefined {
    const next = IsUnsetCalendarDate(value) ? undefined : ParseCalendarDate(value) ?? undefined;
    return next?.getTime() === current?.getTime() ? current : next;
  }

  /**
   * The adoption is only ours to change while nothing else has decided it. Once an actor answered the Adopt
   * action, the backend overlays that answer on every read, so an edit here would appear to save and then
   * silently revert.
   */
  get adoptionIsEditable(): boolean {
    return this.xbrlFilingData?.adoptionIsPrefilled !== false;
  }

  /**
   * A filing that was not adopted has no adoption date, so the date is only ours to set while the adoption
   * itself says yes - and only while the adoption is editable at all.
   */
  get adoptionDateIsEditable(): boolean {
    return this.adoptionIsEditable && this.xbrlFilingData?.documentAdoptionStatus === true;
  }

  get hasMissingFields(): boolean {
    return this.missingFieldLabels.length > 0;
  }

  get isAccepted(): boolean {
    return this.isAcceptedControl.value === true;
  }

  /**
   * Complete data saves as it always did; incomplete data only once the user accepted it.
   * <p>Field level validation is left to say its piece through the error messages, as it always did, rather
   * than blocking the save too: a value can be both missing and malformed - whitespace is - and blocking on
   * malformed would leave the accept checkbox showing with no way to get past it.</p>
   */
  get canSave(): boolean {
    return !this.hasMissingFields || this.isAccepted;
  }

  setDropdownValue(property: keyof XbrlFilingData, value: boolean | LegalEntityRegisteredTypeEnum | FilingDataLanguageEnum) {
    (this.xbrlFilingData as any)[property] = value;
    if (property === 'documentAdoptionStatus') {
      this.followAdoptionStatus();
    }
    // the dropdowns write straight to the model, past the form, so nothing else would recompute
    this.refreshDerivedState();
  }

  /**
   * Keeps the adoption date in step with the adoption itself: a filing that was not adopted carries no
   * adoption date, so answering 'no' clears it rather than filing a date alongside 'not adopted'.
   * <p>The date is set aside rather than thrown away, so answering 'yes' again does not cost the user the date
   * they already had.</p>
   */
  private followAdoptionStatus(): void {
    if (this.xbrlFilingData.documentAdoptionStatus) {
      this.xbrlFilingData.documentAdoptionDate ??= this.setAsideAdoptionDate;
      return;
    }
    this.setAsideAdoptionDate = this.xbrlFilingData.documentAdoptionDate;
    this.xbrlFilingData.documentAdoptionDate = undefined;
  }

  close() {
    this.closeModal.emit(null);
  }

  async save() {
    if (!this.canSave) {
      return;
    }
    try {
      (await this.filingDataApiClient.update(this.requestId, this.toRequestBody())).subscribe({
        next: () => {
          this.toastService.showSuccess(this.translations.success, this.translations.filingDataUpdated);
          this.closeModal.emit(this.xbrlFilingData);
        },
        error: (error: any) => {
          console.error("Error updating filing data:", error);
          this.toastService.showError(this.translations.error, this.translations.filingDataUpdateFailed);
        }
      });
    } catch (error) {
      console.error("Error updating filing data:", error);
      this.toastService.showError(this.translations.error, this.translations.filingDataUpdateFailed);
    }
  }

  markFormAsDirty() {
    this.form.markAsDirty();
  }

  convertBooleanToString(value: boolean): string {
    return value ? this.translateService.instant('Yes') : this.translateService.instant('No');
  }

  updateDate(property: 'financialReportingPeriodEndDate' | 'documentAdoptionDate', event: any) {
    const picked: Date | null = event instanceof Date && !isNaN(event.getTime()) ? event : null;
    if (!picked) {
      // the picker was closed without a date being chosen
      return;
    }
    const dateString = ToCalendarDateString(picked); //YYYY-MM-DD, the picked calendar day itself
    if(property === 'financialReportingPeriodEndDate') {
      // the control is part of the form, so valueChanges copies it onto the model
      this.financialReportingPeriodEndDateControl.setValue(dateString);
      return;
    }
    this.xbrlFilingData.documentAdoptionDate = dateString as unknown as Date;
    // written straight to the model, past the form, so nothing else would recompute
    this.refreshDerivedState();
    this.markFormAsDirty();
  }

  public formatDate(date: Date | undefined): string {
    const calendarDate = ParseCalendarDate(date);
    if (!calendarDate || IsUnsetCalendarDate(date)) return '';

    // Format date to '31 december 2024'
    const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'long', year: 'numeric' };
    const currentLang = this.translateService.currentLang || 'nl-NL';
    return calendarDate.toLocaleDateString(currentLang, options);
  }

  private isFieldMissing(field: keyof XbrlFilingData): boolean {
    if (field === 'financialReportingPeriodEndDate') {
      return IsUnsetCalendarDate(this.xbrlFilingData.financialReportingPeriodEndDate);
    }
    const value = (this.xbrlFilingData as any)[field];
    return typeof value !== 'string' || value.trim() === '';
  }

  /**
   * What we post back. 'isInitial' is the server's to set (it marks the data as hand edited), and 'isValid' and
   * 'hasPrefilledAdoptionDate' are computed there, so none of them belong in the body.
   */
  private toRequestBody(): XbrlFilingData {
    const { isInitial, isValid, hasPrefilledAdoptionDate, ...body } = this.xbrlFilingData;
    return body as XbrlFilingData;
  }

  private determineLegalEntityRegisteredType(registeredType: string) {
    switch(registeredType?.toLocaleLowerCase()) {
      case "micro":
        this.xbrlFilingData.legalEntityRegisteredType = LegalEntityRegisteredTypeEnum.Micro;
        break;
      case "small":
      case "klein":
        this.xbrlFilingData.legalEntityRegisteredType = LegalEntityRegisteredTypeEnum.Small;
        break;
      case "medium":
      case "middelgroot":
        this.xbrlFilingData.legalEntityRegisteredType = LegalEntityRegisteredTypeEnum.Medium;
        break;
      case "large":
      case "groot":
        this.xbrlFilingData.legalEntityRegisteredType = LegalEntityRegisteredTypeEnum.Large;
        break;
      default:
        this.xbrlFilingData.legalEntityRegisteredType = LegalEntityRegisteredTypeEnum.Large; // Default value if no match is found
    }
  }

  /** The API sends 'nl'/'en' but has carried 'Nl' as its default, and the value is part of 'isValid' */
  private determineLanguage(language: string) {
    this.xbrlFilingData.language = language?.toLocaleLowerCase() === FilingDataLanguageEnum.En
      ? FilingDataLanguageEnum.En
      : FilingDataLanguageEnum.Nl;
  }
}
